import type { ResourceMap } from '@hostesa/i18n';

/**
 * App-level i18n resources for the backoffice operator console, merged on top
 * of the bundled `@hostesa/i18n` `common` namespace via the `backoffice`
 * namespace.
 *
 * Every key MUST exist in all four supported locales — `@hostesa/i18n` throws
 * on a missing key in `test` and `production` modes (key-parity enforcement),
 * so adding a string here without a translation will fail the build/tests.
 * Non-`en` locales start as English placeholders where a real translation is
 * not yet available; this is the documented "temporary" state.
 */

const en = {
  appName: 'Hostesa Operator',
  shell: {
    operatorConsole: 'Operator console',
    crossTenant: 'Cross-tenant view',
    signedInAs: 'Signed in as',
    operator: 'Operator',
    search: 'Search…',
    backToPlatform: 'Back to platform',
    opsBadge: 'OPS',
    internal: 'Hostesa internal',
  },
  commandPalette: {
    title: 'Command palette',
    triggerLabel: 'Search…',
    placeholder: 'Search the operator console…',
    empty: 'No matching destinations.',
    navHeading: 'Go to',
  },
  navGroups: {
    overview: 'Overview',
    customers: 'Customers',
    operations: 'Operations',
    runtime: 'Runtime',
    providers: 'Providers',
    finance: 'Finance',
    governance: 'Governance',
    provisioning: 'Provisioning',
    // Retained for back-compat; no longer used as group labels.
    oversight: 'Oversight',
    platform: 'Platform',
    diagnostics: 'Diagnostics',
    support: 'Support',
  },
  nav: {
    overview: 'Overview',
    register: 'Register partner',
    support: 'Support',
    users: 'Users',
    auditLog: 'Audit log',
    featureFlags: 'Feature flags',
    tenants: 'Tenants',
    hotels: 'Hotels',
    calls: 'Calls',
    incidents: 'Incidents',
    queues: 'Queues',
    events: 'Events',
    costs: 'Costs',
    providers: 'Provider diagnostics',
  },
  pages: {
    tenants: {
      title: 'Tenants',
      description: 'Cross-tenant directory of every Hostesa customer account.',
    },
    hotels: {
      title: 'Hotels',
      description: 'Every hotel across all tenants, for operator oversight.',
    },
    calls: {
      title: 'Calls',
      description: 'Cross-tenant call diagnostics. Guest content is redacted.',
    },
    incidents: {
      title: 'Incidents',
      description: 'Open and resolved operational incidents across the platform.',
    },
    queues: {
      title: 'Queues',
      description: 'Background job and processing queue health.',
    },
    events: {
      title: 'Events',
      description: 'Platform-wide system event stream for diagnostics.',
    },
    costs: {
      title: 'Costs',
      description: 'Usage and cost attribution across tenants.',
    },
    providers: {
      title: 'Provider diagnostics',
      description:
        'Low-level provider telemetry. This is the ONLY surface where provider metadata appears.',
    },
    users: {
      title: 'Users',
      description: 'Cross-tenant directory of every operator and hotel user account.',
    },
    auditLog: {
      title: 'Audit log',
      description: 'Cross-tenant, append-only record of sensitive and high-risk actions.',
    },
    featureFlags: {
      title: 'Feature flags',
      description: 'Roll-out switches for platform capabilities across tenants.',
    },
  },
  register: {
    eyebrow: 'Provisioning',
    title: 'Register a partner',
    description:
      'Onboard a new partner — create the partner, its first hotel, and the first admin.',
    steps: {
      partner: 'Partner',
      hotel: 'Hotel',
      admin: 'First admin',
      review: 'Review',
    },
    fields: {
      partnerName: 'Partner name',
      partnerNamePlaceholder: 'e.g. Vila Rila Group',
      partnerSlug: 'Partner slug',
      slugPlaceholder: 'lowercase-with-hyphens',
      hotelName: 'Hotel name',
      hotelNamePlaceholder: 'e.g. Vila Rila Sofia',
      hotelSlug: 'Hotel slug',
      timezone: 'Timezone',
      timezonePlaceholder: 'e.g. Europe/Sofia',
      adminEmail: 'Admin email',
      adminEmailPlaceholder: 'admin@partner.example',
      adminName: 'Admin full name',
      adminNamePlaceholder: 'optional',
      password: 'Temporary password',
      passwordHelp: 'At least 10 characters. The admin rotates it after first login.',
    },
    actions: {
      back: 'Back',
      next: 'Next',
      create: 'Register partner',
      creating: 'Registering…',
      registerAnother: 'Register another',
    },
    review: {
      title: 'Review',
      partner: 'Partner',
      hotel: 'Hotel',
      admin: 'Admin',
    },
    errors: {
      required: 'This field is required.',
      slug: 'Use lowercase letters, numbers and single hyphens.',
      email: 'Enter a valid email address.',
      passwordShort: 'Use at least 10 characters.',
      slugTaken: 'That slug is already taken. Choose another.',
      generic: 'Could not register the partner. Please try again.',
    },
    success: {
      title: 'Partner registered',
      description: 'The partner, its first hotel, and the admin account are ready.',
      partnerLabel: 'Partner',
      hotelLabel: 'Hotel',
      adminLabel: 'Admin',
    },
    checklist: {
      title: 'Next: set up the hotel',
      voice: 'Voice configuration',
      phone: 'Phone numbers',
      knowledge: 'Knowledge onboarding',
      notConfigured: 'Not configured yet',
      openSetup: 'Open setup',
    },
  },
  table: {
    column: {
      name: 'Name',
      tenant: 'Tenant',
      hotel: 'Hotel',
      status: 'Status',
      updated: 'Updated',
      provider: 'Provider',
      detail: 'Detail',
    },
  },
  oversight: {
    notAvailable: 'N/A',
    clear: 'Clear filters',
    loading: 'Loading…',
    error: {
      title: 'Could not load data',
      description: 'Something went wrong while loading. Please try again.',
    },
    pagination: {
      range: '{{from}}–{{to}} of {{total}}',
      prev: 'Previous',
      next: 'Next',
      perPage: 'Per page',
    },
    tabsAll: 'All',
    columns: {
      tenant: 'Tenant',
      hotels: 'Hotels',
      plan: 'Plan',
      calls24h: 'Calls 24h',
      lines: 'Lines',
      mfa: 'MFA',
      status: 'Status',
      hotel: 'Hotel',
      callId: 'Call ID',
      lang: 'Lang',
      state: 'State',
      duration: 'Dur',
    },
    callDetail: {
      title: 'Call details',
      id: 'Call ID',
      hotel: 'Hotel',
      tenant: 'Partner',
      direction: 'Direction',
      language: 'Language',
      status: 'State',
      startedAt: 'Started',
      duration: 'Duration',
      close: 'Close',
      viewFull: 'View full details →',
    },
    callStateGroups: {
      inProgress: 'In progress',
      completed: 'Completed',
      failed: 'Failed',
    },
    tenants: {
      searchPlaceholder: 'Search by name or slug…',
      registerCta: 'Register partner',
      metrics: {
        total: 'Tenants',
      },
      statusAll: 'All statuses',
      planAll: 'All plans',
      status: {
        active: 'Active',
        suspended: 'Suspended',
        cancelled: 'Cancelled',
      },
      plan: {
        starter: 'Starter',
        growth: 'Growth',
        pro: 'Pro',
      },
      empty: {
        title: 'No tenants yet',
        description: 'Provisioned partners will appear here.',
      },
      emptyFiltered: {
        title: 'No matching tenants',
        description: 'No partners match the current filters.',
      },
    },
    hotels: {
      searchPlaceholder: 'Search by hotel name or slug…',
      unknownPartner: 'Unknown partner',
      metrics: {
        total: 'Hotels',
      },
      status: {
        draft: 'Draft',
        active: 'Active',
        suspended: 'Suspended',
      },
      empty: {
        title: 'No hotels yet',
        description: 'Hotels across all partners will appear here.',
      },
      emptyFiltered: {
        title: 'No matching hotels',
        description: 'No hotels match the current filters.',
      },
    },
    calls: {
      searchPlaceholder: 'Search by call id, partner or hotel…',
      metrics: {
        total: 'Calls',
      },
      statusAll: 'All statuses',
      directionAll: 'All directions',
      languageAll: 'All languages',
      status: {
        created: 'Created',
        ringing: 'Ringing',
        answered: 'Answered',
        ai_active: 'AI active',
        handoff_requested: 'Handoff requested',
        human_active: 'Human active',
        completed: 'Completed',
        failed: 'Failed',
      },
      direction: {
        inbound: 'Inbound',
        outbound: 'Outbound',
      },
      empty: {
        title: 'No calls yet',
        description: 'Cross-tenant call diagnostics will appear here.',
      },
      emptyFiltered: {
        title: 'No matching calls',
        description: 'No calls match the current filters.',
      },
    },
    users: {
      searchPlaceholder: 'Search by name, email or scope…',
      metrics: {
        operators: 'Operators',
        hotelUsers: 'Hotel users',
      },
      tabs: {
        all: 'All',
        operators: 'Operators',
        hotel: 'Hotel users',
      },
      columns: {
        user: 'User',
        email: 'Email',
        scope: 'Scope',
        status: 'Status',
      },
      roles: {
        hostesa_admin: 'Operator admin',
        hostesa_support: 'Support',
        hostesa_readonly: 'Read-only',
        hotel_admin: 'Hotel admin',
        hotel_staff: 'Hotel staff',
        none: 'No role',
      },
      status: {
        invited: 'Invited',
        active: 'Active',
        disabled: 'Disabled',
      },
      mfa: {
        on: 'On',
        off: 'Off',
      },
      internalScope: 'Hostesa internal',
      unknownScope: 'No scope',
      reveal: 'Reveal',
      hide: 'Hide',
      maskedNote: 'Emails are masked by default.',
      empty: {
        title: 'No users yet',
        description: 'Operator and hotel user accounts will appear here.',
      },
      emptyFiltered: {
        title: 'No matching users',
        description: 'No users match the current filters.',
      },
    },
    userDetailPage: {
      eyebrow: 'Customers',
      crumb: 'Users',
      backToUsers: 'Back to users',
      sampleNote: 'Internal diagnostic · sample',
      meta: { role: 'Role', id: 'ID' },
      identity: {
        title: 'Identity',
        userId: 'User ID',
        email: 'Email',
        role: 'Role',
        scope: 'Scope',
        mfa: 'MFA',
        mfaEnabled: 'Enabled',
        lastSeen: 'Last seen',
      },
      actions: {
        resetMfa: 'Reset MFA',
        suspend: 'Suspend',
      },
      activity: {
        title: 'Recent activity',
      },
      notFound: {
        title: 'User not found',
        description: 'This user is not in the current directory page.',
      },
    },
    tenantDetail: {
      eyebrow: 'Operations',
      crumb: 'Tenants',
      hotelsTitle: 'Hotels',
      callsTitle: 'Recent calls',
      hotelsEmpty: 'No hotels for this partner yet.',
      callsEmpty: 'No calls for this partner yet.',
      backToTenants: 'Back to tenants',
      resync: 'Re-sync',
      kpi: {
        hotels: 'Hotels',
        calls24h: 'Calls 24h',
        plan: 'Plan',
      },
    },
    hotelDetail: {
      eyebrow: 'Operations',
      crumb: 'Hotels',
      partnerLabel: 'Partner',
      callsTitle: 'Recent calls',
      callsEmpty: 'No calls for this hotel yet.',
      backToHotels: 'Back to hotels',
      resync: 'Re-sync',
      kpi: {
        calls24h: 'Calls 24h',
        tenant: 'Tenant',
        linesOnline: 'Lines online',
        region: 'Region',
      },
      openSupportSession: 'Open support session',
      closeSupportSession: 'Close support session',
      diagnostics: {
        title: 'Diagnostics',
        sampleNote: 'Internal diagnostic · sample',
        provider: 'Provider',
        sipTrunk: 'SIP trunk',
        lastHeartbeat: 'Last heartbeat',
        knowledgeBase: 'Knowledge base',
        activeScripts: 'Active scripts',
      },
    },
    hotelSetup: {
      title: 'Hotel setup',
      description:
        'The operational facts the AI receptionist answers from. Edits here act on behalf of the partner.',
      loading: 'Loading hotel setup…',
      errorTitle: 'Could not load hotel setup',
      errorDescription: 'The setup facts for this hotel could not be fetched. Try again shortly.',
      readOnlyNote: 'Your role can view setup facts but not edit them.',
      auditNote: 'Edits are made on behalf of the partner and are recorded in the audit log.',
      required: {
        title: 'Required for activation',
        description: 'The onboarding activation gate needs all four before a run can be approved.',
      },
      goodToHave: {
        title: 'Good to have',
        description: 'Facts guests frequently call about.',
      },
      activationComplete: 'Activation facts complete',
      status: { set: 'Set', missing: 'Missing' },
      save: 'Save',
      saving: 'Saving…',
      saved: 'Saved',
      saveError: 'Could not save',
      clear: 'Clear',
      clearing: 'Clearing…',
      clearError: 'Could not clear',
      categories: {
        cancellation_policy: {
          label: 'Cancellation policy',
          description: 'How far ahead a guest can cancel and any fees that apply.',
        },
        check_in_time: {
          label: 'Check-in time',
          description: 'The earliest time a guest can check in (e.g. 15:00).',
        },
        check_out_time: {
          label: 'Check-out time',
          description: 'The latest time a guest must check out by (e.g. 11:00).',
        },
        transfer_number: {
          label: 'Transfer number',
          description: 'The phone number the AI transfers callers to for the front desk.',
        },
        breakfast_hours: {
          label: 'Breakfast hours',
          description: 'When breakfast is served (e.g. 07:00–10:30).',
        },
        dinner_hours: {
          label: 'Dinner hours',
          description: 'When dinner is served in the restaurant.',
        },
        spa_hours: {
          label: 'Spa hours',
          description: 'Opening hours for the spa / wellness area.',
        },
        pool_hours: {
          label: 'Pool hours',
          description: 'When the pool is open to guests.',
        },
        parking: {
          label: 'Parking',
          description: 'Parking options, availability, and any charges.',
        },
        location_directions: {
          label: 'Directions & location',
          description: 'How to reach the hotel, and its address / GPS coordinates.',
        },
        lost_and_found: {
          label: 'Lost & found',
          description: 'How guests can report or reclaim lost items.',
        },
        pets_policy: {
          label: 'Pets policy',
          description: 'Whether pets are allowed and any conditions or fees.',
        },
      },
    },
    callDetailPage: {
      eyebrow: 'Operations',
      crumb: 'Calls',
      title: 'Call {{shortId}}',
      backToCalls: 'Back to calls',
      recording: 'Recording',
      exportLog: 'Export log',
      notFound: {
        title: 'Call not found',
        description: 'This call is not in the recent diagnostics window.',
      },
      internalNotice: {
        label: 'Internal diagnostic view',
        body: 'Operator-only diagnostics. No transcript or guest content is shown.',
      },
      meta: {
        hotel: 'Hotel',
        cost: 'Cost',
      },
      trace: 'Trace',
      tabs: {
        overview: 'Overview',
        recording: 'Recording',
        transcript: 'Transcript',
        diagnostics: 'Diagnostics',
      },
      canonical: {
        title: 'Canonical record',
        callId: 'Hostesa call ID',
        hotel: 'Hotel',
        caller: 'Caller',
        callerRedacted: 'redacted',
        language: 'Language',
        state: 'State',
        duration: 'Duration',
        costEstimate: 'Cost estimate',
      },
      sampleNote: 'Internal diagnostic · sample',
      recordingSection: {
        title: 'Recording',
      },
      transcript: {
        title: 'Transcript',
      },
      diagnostics: {
        title: 'Provider diagnostics',
        provider: 'Provider',
        session: 'Session',
        stt: 'STT',
        tts: 'TTS',
        webhook: 'Webhook',
      },
      rawPayload: {
        title: 'Raw provider payload',
      },
      toolTimeline: {
        title: 'Tool timeline',
      },
    },
    impersonate: {
      title: 'Impersonate',
      description: 'Open a scoped session as a member of this hotel for support.',
      noPermission: 'You do not have permission to impersonate.',
      memberLabel: 'Member',
      membersEmpty: 'No members to impersonate.',
      membersLoading: 'Loading members…',
      start: 'Start impersonation',
      starting: 'Opening session…',
      started: 'Impersonation session opened in a new tab.',
      error: 'Could not start impersonation. Please try again.',
      reasonLabel: 'Reason',
      reasonPlaceholder: 'Why you need to act as this user (min. 12 characters)',
      reasonHint: 'Recorded in the audit log. 12–500 characters.',
      reasonTooShort: 'Please give a reason of at least 12 characters.',
      ticketLabel: 'Support ticket',
      ticketPlaceholder: 'e.g. HOS-4830',
      ticketHint: 'A tracked support ticket key, like HOS-4830.',
      ticketInvalid: 'Enter a valid ticket key, like HOS-4830.',
      durationLabel: 'Session length',
      duration15: '15 minutes',
      duration30: '30 minutes',
      duration60: '1 hour',
      selectMemberFirst: 'Select a member to impersonate first.',
    },
  },
  overview: {
    eyebrow: 'Backoffice',
    title: 'Overview',
    degraded: 'Degraded — 2 incidents',
    allIncidents: 'All incidents',
    fullAuditLog: 'Full audit log',
    costAnomaly: 'Cost anomaly — Sunny Coast Group at €4,020 MTD, 38% above forecast.',
    inspect: 'Inspect',
    env: {
      environment: 'Env',
      deploy: 'Deploy',
      region: 'Region',
    },
    preview: {
      label: 'Operations preview.',
      body: 'System posture, incidents, provider health, costs and the audit feed are sample telemetry until their operator endpoints ship.',
    },
    sections: {
      posture: 'System posture',
      incidents: 'Active incidents',
      providerHealth: 'Provider health',
      audit: 'Recent high-risk audit events',
    },
  },
  support: {
    title: 'Support',
    description: 'Help a hotel without unsafe database or provider access.',
    safety: {
      label: 'You act as the user — safely.',
      body: 'Sessions are short-lived, scoped to one hotel, exclude destructive actions, attribute both you and the impersonated user, and always show a banner in the platform.',
    },
    start: {
      title: 'Start an impersonation session',
      description: 'Choose a partner and hotel, pick a member, and give a reason and ticket.',
      tenantLabel: 'Partner',
      tenantPlaceholder: 'Select a partner…',
      hotelLabel: 'Hotel',
      hotelPlaceholder: 'Select a hotel…',
      selectScopeHint: 'Select a partner and a hotel to choose a member to impersonate.',
    },
    context: {
      title: 'Support context',
      description:
        'Line status, open incidents, onboarding and integrations for the selected hotel.',
      emptyTitle: 'Not connected yet',
      emptyDescription:
        'Live support context will appear here once the operator support-context endpoint is available.',
    },
  },
  empty: {
    tenants: {
      title: 'No tenants to show',
      description: 'Tenant oversight data will appear here once available from the backend.',
    },
    hotels: {
      title: 'No hotels to show',
      description: 'Hotel oversight data will appear here once available from the backend.',
    },
    calls: {
      title: 'No calls to show',
      description: 'Cross-tenant call diagnostics will appear here once available.',
    },
    incidents: {
      title: 'No incidents',
      description: 'There are no operational incidents to review right now.',
    },
    queues: {
      title: 'No queue data',
      description: 'Queue health metrics will appear here once available.',
    },
    events: {
      title: 'No events',
      description: 'The platform event stream is empty right now.',
    },
    costs: {
      title: 'No cost data',
      description: 'Cost attribution will appear here once usage data is available.',
    },
    providers: {
      title: 'No provider telemetry',
      description: 'Provider diagnostics will appear here when enabled and available.',
    },
    users: {
      title: 'No users to show',
      description:
        'A cross-tenant user directory will appear here once the operator users endpoint exists.',
    },
    auditLog: {
      title: 'Audit log not connected yet',
      description:
        'The cross-tenant audit feed will appear here once the operator audit endpoint exists.',
    },
    featureFlags: {
      title: 'No feature flags',
      description:
        'Feature-flag controls will appear here once the operator flags endpoint exists.',
    },
  },
  providerNotice: {
    label: 'Provider metadata — operators only',
    body: 'This page exposes raw provider details for diagnostics. It is never shown in the hotel-facing platform.',
  },
  diagnostics: {
    preview: {
      label: 'Preview — sample data',
      body: 'These figures are a clearly-labelled sample fixture. They are not live until the operator endpoint ships.',
    },
    tabs: {
      all: 'All',
    },
    incidents: {
      activeMetric: '{{count}} active',
      tabs: {
        all: 'All',
        high: 'High',
        med: 'Med',
        low: 'Low',
      },
      columns: {
        id: 'ID',
        severity: 'Severity',
        incident: 'Incident',
        age: 'Age',
        owner: 'Owner',
        status: 'Status',
      },
    },
    queues: {
      metric: 'Backed up {{backedUp}} · Dead-letter {{deadLetter}}',
      summary: {
        depth: 'deep',
        backedUp: 'backed up',
        healthy: 'healthy',
      },
      columns: {
        queue: 'Queue',
        rate: 'Rate',
        failPct: 'Fail %',
        depth: 'Depth',
        p95: 'P95',
      },
    },
    events: {
      tabs: {
        all: 'All',
        info: 'Info',
        warning: 'Warning',
        error: 'Error',
      },
      columns: {
        time: 'Time',
        type: 'Type',
        tenant: 'Tenant',
        payload: 'Payload',
      },
      footnote:
        'Canonical events are operational facts · read-only · payloads redacted by default.',
    },
    providers: {
      degradedMetric: '{{count}} degraded',
      status: {
        operational: 'Operational',
        elevated: 'Elevated latency',
      },
      stats: {
        uptime: 'Uptime',
        p95: 'P95',
        errRate: 'Err rate',
        sessions: 'Sessions',
      },
    },
    costs: {
      mtdMetric: 'MTD {{value}}',
      billingCaveat: 'Internal operational cost view. Not customer billing.',
      columns: {
        tenant: 'Tenant',
        stt: 'STT',
        llm: 'LLM',
        telephony: 'Telephony',
        totalMtd: 'Total MTD',
      },
    },
    audit: {
      tabs: {
        all: 'All',
        high: 'High-risk',
        medium: 'Medium',
        low: 'Low',
      },
      columns: {
        time: 'Time',
        actor: 'Actor',
        action: 'Action',
        resource: 'Resource',
        risk: 'Risk',
        outcome: 'Outcome',
      },
      footnote: 'Append-only · no edit actions · exports are redacted and audited.',
    },
    featureFlags: {
      metric: 'Production {{production}} · Staging {{staging}}',
      caveat:
        'Production changes require a reason and are written to the audit log. High-risk flags may require a second approval.',
      columns: {
        flag: 'Flag',
        risk: 'Risk',
        rollout: 'Rollout',
        scope: 'Scope',
        owner: 'Owner',
        state: 'State',
      },
    },
  },
  login: {
    title: 'Operator sign in',
    subtitle: 'Hostesa internal operator console.',
    submitting: 'Signing in…',
    emailRequired: 'Enter your email address.',
    emailInvalid: 'Enter a valid email address.',
    passwordRequired: 'Enter your password.',
  },
  impersonation: {
    bannerLabel: 'Impersonation active',
    actingAs: 'Acting as',
    since: 'since',
    stop: 'Stop impersonating',
    start: 'Impersonate',
  },
  noAccess: {
    title: 'Operator access required',
    description:
      'This console is restricted to Hostesa operators. Your account does not have operator access.',
    backToLogin: 'Back to sign in',
  },
  notFound: {
    title: 'Page not found',
    description: 'We could not find what you were looking for.',
    backToLogin: 'Back to sign in',
  },
} as const;

/**
 * The full resource map. Non-English locales reuse the English copy as a
 * temporary placeholder so the key-parity check passes; translators replace
 * these strings without changing the key shape.
 */
export const APP_I18N_RESOURCES: ResourceMap = {
  en: { backoffice: en },
  bg: { backoffice: en },
  de: { backoffice: en },
  fr: { backoffice: en },
};
